/**
 * Realtime event ledger — the tablet half of the "push = hint, ledger = truth" model.
 *
 * Every event published by the backend since server_manager 2.12.0 carries a dense
 * per-channel `seq` (assigned by Postgres when the event is written to
 * bronze.realtime_event). This module tracks the last contiguously-applied seq per
 * channel (the cursor) and recovers anything the WebSocket dropped by querying
 * bronze.events_since(channel, cursor) through GraphQL:
 *
 *   - seq == cursor+1  → apply, advance cursor (the normal live path)
 *   - seq  > cursor+1  → apply now (latency first), do NOT advance, resync the gap
 *   - seq <= cursor    → duplicate/out-of-order push → discard...
 *     ...UNLESS it looks like a seq reset (seq == 1, or far below the cursor, with an
 *     unseen event_id). Dead channels (>30 days without events) get their seq counter
 *     deleted server-side, so the next event restarts at 1; without this check the
 *     tablet would silently discard every event after a reset, forever.
 *
 * Resync replay of a normal channel is at-most-once: cursor + dedup are persisted BEFORE
 * the handler runs, so a replayed `refresh` action (which reloads the page) can never loop.
 * The live push path stays at-least-once: ack/cursor only after the handler succeeds, so a
 * failed apply is retried (armRetry, then the next resync).
 *
 * The `orders` channel opts into coalesced resync: its handler ignores gap payloads and
 * refetches everything from the DB anyway, so replaying N missed events one by one would
 * mean N full fetches — and worse, replaying an OLD order event after a NEWER pushed one
 * would overwrite fresh state with stale state. Instead a single full fetch
 * (`applyFn(null)`) closes the resync, and the gap rows are acked only AFTER it succeeds:
 * at-least-once. The walk advances a LOCAL cursor and anchor, committed only after the refetch
 * succeeded and the ACKs were enqueued, so no persist in between (a push applied on another
 * channel, a seed) can store a cursor past rows that were never ACKed. A failed refetch has
 * nothing to rewind: the committed cursor never moved, and the retry walks the same rows.
 *
 * Failure semantics mirror the backend client: if events_since is unreachable (offline,
 * or the GraphQL schema doesn't expose it yet) live pushes keep applying through the
 * gap branch and the resync retries with backoff — never worse than the pre-ledger app.
 *
 * Persistence is localStorage (same rationale as ackQueue.js): synchronous writes that
 * survive both page death and the service-worker update that wipes IndexedDB. The
 * deviceId travels with the state so a session logged in as a different device never
 * inherits another tablet's cursors. The state is versioned, validated on load, and expires
 * after 72 h (the ledger's own retention: older events are gone, so an older cursor is moot).
 */
import { Hub } from 'aws-amplify/utils';
import Logger from '@/logger';
import { enqueueAck, enqueueAcks, flushAcks } from './ackQueue.js';
import { reportDeviceEvent } from './deviceEvents.js';
import { reportForensicEvent } from './forensicEvents.js';
import { fetchEventsSince, fetchEventsSinceTime, fetchChannelMaxSeq } from './graphql.js';
import { WS_OPEN, SOCKET_STATUS_CLOSED, SOCKET_STATUS_READY, REBUILD_TIMEOUT_MS } from './socketWatchdog.js';

const logger = new Logger({ screen: 'eventLedger' });

const STORAGE_KEY = 'bronze_event_ledger';
const DEDUP_MAX = 500; // event_ids remembered (ring); enough for hours of traffic on one tablet
const PAGE_SIZE = 100; // bronze.events_since LIMIT — exactly 100 rows means "ask again"
const RESET_MIN_GAP = 100; // seq this far below the cursor with an unseen id = server-side reset
const MAX_APPLY_AGE_MS = 60 * 60 * 1000; // replayed modals/actions older than this: ack but don't apply
const RETRY_BASE_MS = 30000;
const RETRY_MAX_MS = 300000;
// Hint reasons say "the app may have missed something" without evidence: a flapping tab or link
// fires them every few seconds. Each channel takes one per window, the rest wait for its end.
const HINT_REASONS = new Set(['visible', 'online', 'reconnected']);
const HINT_MIN_INTERVAL_MS = 30000;
// How far before boot the send-time anchor starts. It is only the FLOOR for a cursorless
// recovery (once the first event is applied the anchor advances to real server send-times),
// so its only job is to absorb device↔server clock skew on that very first recovery without
// pulling much history. Over-fetch is bounded by MAX_APPLY_AGE_MS + dedup + orders coalescing.
const ANCHOR_MARGIN_MS = 5 * 60 * 1000;
const LEDGER_RETENTION_MS = 72 * 3600 * 1000; // realtime_event retention: older persisted state is stale
const SCHEMA_VERSION = 2; // persisted shape; a newer one (a rollback) is discarded, not guessed at

let deviceId = null;
let channels = new Map(); // path -> { name, applyFn, coalesceResync }
let cursors = {}; // path -> last contiguously-applied seq
let anchors = {}; // path -> ISO send-time high-water-mark; the cursorless-resync anchor
let seen = []; // event_id ring (insertion order)
let seenSet = new Set();
let validated = new Set(); // paths whose persisted cursor was already checked against the server
let persistFailedReported = false;
let savedAt = null; // ms of the last write (or of the state loaded), for diagnostics
let resyncing = new Set(); // paths with a resync in flight
let rerunAfter = new Set(); // paths that got a gap WHILE resyncing — run once more
let retryTimers = {};
let retryMs = {};
let lastSyncAt = {}; // path -> ms a resync last started (any reason): the hint window's anchor
let hintTimers = {}; // path -> { timer, reason }: the one trailing hint resync waiting out the window
let listenersArmed = false;
let hubUnsubscribe = null;
let lastConnectionState = null;
let onConnectivity = null; // callback(isOnline: boolean), set by initEventLedger
let onSocketState = null;  // callback({state, previous}), set by initEventLedger

// ── the socket itself (read-only probe for the local watchdog) ───────────────────────────────
// Every Hub `ConnectionStateChange` payload carries `data.provider`: Amplify's singleton
// AppSyncEventProvider. Its PRIVATE fields (`awsRealTimeSocket`, `socketStatus`,
// `keepAliveTimestamp`) are the only ground truth about the socket, because the Hub can be
// wrong for hours (stale handshake timer, halted reconnection — see socketWatchdog.js). They
// are private, so aws-amplify and @aws-amplify/api are pinned to exact versions in package.json
// and tests/unit/socketWatchdog.amplify.spec.js runs the real provider to fail when one moves.
// Nothing here writes to the provider and nothing here feeds computeIsOnline.
const EVENTS_PROVIDER_NAME = 'AWSAppSyncEventsProvider';
// Amplify's CONTROL_MSG.SUBSCRIPTION_ACK; pinned by tests/unit/socketWatchdog.amplify.spec.js.
const SUBSCRIPTION_ACK_EVENT = 'Subscription ack';
let socketProvider = null;   // latest provider reference from a Hub payload
let trackedSocket = null;    // identity of provider.awsRealTimeSocket as last observed
let socketOpenedAt = null;   // when this socket object was first observed (ms)
let socketClosedAt = null;   // when this socket object fired `close` (ms)
let socketSeq = 0;           // bumped whenever the provider's socket object changes: an identity, not a clock
let notReadySince = null;    // since when the socket has not been open-and-ready (ms)
let hubStateSince = null;    // when the Hub last changed state (ms)
let rebuildTracking = null;  // while a socket rebuild runs: { started, acked, fallback } per channel path
let lateTracking = null;     // the last rebuild's tracking, kept for LATE_ACK_WINDOW_MS after it ends (an ack slower than the wait)
let lateTrackingTimer = null;
const LATE_ACK_WINDOW_MS = 60000;

// Single source of truth for "does the app have real connectivity right now?".
// Connectivity is the OS interface signal ONLY (navigator.onLine). The Events
// WebSocket's ConnectionState is deliberately NOT consulted here: Amplify cycles the
// socket through Connecting / ConnectionDisrupted / Disconnected as a normal part of
// its lifecycle (idle keep-alive, tab backgrounding, silent reconnects) while HTTP —
// photo uploads and /item-comparison — keeps working perfectly. Gating connectivity on
// the socket state made a healthy tablet read "offline" every time the socket was
// mid-cycle, so analyses were wrongly queued ("Se analizará al reconectar") and the
// verdict/correction screen never opened — a total outage on locales whose internet was
// fine. The socket ConnectionStateChange listener still runs, but only to drive resync
// on reconnect, never to decide connectivity. navigator.onLine reporting true on a
// dead-uplink kiosk AP degrades gracefully — the HTTP timeout/queue path handles it —
// which is strictly better than the false-offline outage the socket signal caused.
function computeIsOnline() {
  return navigator.onLine !== false;
}
let lastEmittedOnline = null;
function emitConnectivity() {
  const online = computeIsOnline();
  if (online !== lastEmittedOnline) {
    lastEmittedOnline = online;
    logger.console(`Connectivity → ${online ? 'ONLINE' : 'OFFLINE'} (socket=${lastConnectionState || 'unknown'}, navigator.onLine=${navigator.onLine})`);
  }
  if (onConnectivity) onConnectivity(online);
}

function persist() {
  // Without a device id the state could not be told apart on load: writing it would only
  // overwrite the saved one of this device (a channel registered before the ledger init).
  if (!deviceId) return;
  try {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      v: SCHEMA_VERSION, deviceId, savedAt: now, cursors, anchors, seen,
    }));
    savedAt = now;
  } catch (storageError) {
    logger.console('Could not persist event ledger state', storageError);
    if (!persistFailedReported) {
      persistFailedReported = true;
      reportForensicEvent('ledger_persist_failed', { error: String(storageError) });
    }
  }
}

/**
 * Load this device's saved state. Returns why it did or did not apply, for the init event:
 * 'none' | 'corrupt' | 'other_device' | 'stale' | 'ok' (+ the saved age when known).
 * State written before versioning (no `v`, no `savedAt`) is accepted as is.
 */
function loadPersisted() {
  let raw;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch (storageError) {
    logger.console('Could not read persisted event ledger state', storageError);
    return { reason: 'none', savedAgeMs: null };
  }
  if (!raw) return { reason: 'none', savedAgeMs: null };
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (parseError) {
    logger.console('Persisted event ledger state is not JSON, discarding', parseError);
    return { reason: 'corrupt', savedAgeMs: null };
  }
  if (!parsed || typeof parsed !== 'object' || parsed.v > SCHEMA_VERSION) {
    return { reason: 'corrupt', savedAgeMs: null };
  }
  if (parsed.deviceId !== deviceId) return { reason: 'other_device', savedAgeMs: null }; // another tablet's state
  const now = Date.now();
  const savedAgeMs = Number.isFinite(parsed.savedAt) ? now - parsed.savedAt : null;
  savedAt = Number.isFinite(parsed.savedAt) ? parsed.savedAt : null;
  seen = Array.isArray(parsed.seen) ? parsed.seen.filter((id) => typeof id === 'string') : [];
  seenSet = new Set(seen);
  if (savedAgeMs != null && savedAgeMs > LEDGER_RETENTION_MS) {
    // The events behind these cursors are gone from the ledger: a cursorless recovery is the
    // only honest one. The dedup ring stays, it is still true.
    return { reason: 'stale', savedAgeMs };
  }
  if (parsed.cursors && typeof parsed.cursors === 'object') {
    for (const [path, cursor] of Object.entries(parsed.cursors)) {
      if (Number.isInteger(cursor) && cursor >= 0) cursors[path] = cursor; // 0 is a real cursor
    }
  }
  if (parsed.anchors && typeof parsed.anchors === 'object') {
    for (const [path, anchor] of Object.entries(parsed.anchors)) {
      const at = typeof anchor === 'string' ? Date.parse(anchor) : NaN;
      if (!Number.isFinite(at)) continue;
      // A future anchor (device clock moved back) would hide every event until the clock caught up.
      anchors[path] = at > now ? new Date(now - ANCHOR_MARGIN_MS).toISOString() : anchor;
    }
  }
  return { reason: 'ok', savedAgeMs };
}

// Establish the send-time anchor for a channel that has none yet. A persisted anchor from
// an earlier session is kept as-is (a reload mid-disconnection must NOT reset the floor
// forward and skip what arrived while offline); a truly fresh channel anchors just before
// boot. Query-free — reading the clock cannot fail, which is the whole point over a seq
// anchor (fetchChannelMaxSeq at boot can).
function seedAnchor(path) {
  if (anchors[path]) return;
  anchors[path] = new Date(Date.now() - ANCHOR_MARGIN_MS).toISOString();
  persist();
}

// Advance the anchor to the newest send-time we have actually applied/acked, so a later
// cursorless recovery resumes from real server time (skew-free) instead of the boot floor.
function advanceAnchor(path, createdAt) {
  if (!createdAt) return;
  if (!anchors[path] || createdAt > anchors[path]) anchors[path] = createdAt;
}

function markSeen(eventId) {
  if (!eventId || seenSet.has(eventId)) return;
  seen.push(eventId);
  seenSet.add(eventId);
  while (seen.length > DEDUP_MAX) {
    seenSet.delete(seen.shift());
  }
}

function toSeq(value) {
  // seq arrives as a number over the socket but as a string (BigInt) from GraphQL
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function advanceCursor(path, seq) {
  // Math.max: two async applies can resolve out of order
  cursors[path] = Math.max(cursors[path] ?? 0, seq);
}

function clearRetry(path) {
  if (retryTimers[path]) {
    clearTimeout(retryTimers[path]);
    retryTimers[path] = null;
  }
}

function clearHintTimer(path) {
  if (hintTimers[path]) {
    clearTimeout(hintTimers[path].timer);
    delete hintTimers[path];
  }
}

function armHintTimer(path, reason, delayMs) {
  if (hintTimers[path]) return; // one trailing run per channel, with the reason that asked first
  const timer = setTimeout(() => {
    delete hintTimers[path];
    resyncChannel(path, reason);
  }, delayMs);
  hintTimers[path] = { timer, reason };
}

function armRetry(path) {
  if (retryTimers[path]) return;
  const delay = retryMs[path] || RETRY_BASE_MS;
  retryTimers[path] = setTimeout(() => {
    retryTimers[path] = null;
    resyncChannel(path, 'retry');
  }, delay);
  retryMs[path] = Math.min(delay * 2, RETRY_MAX_MS);
}

// ── live push path ──────────────────────────────────────────────────────────

function applyPush(ch, path, data, eventId, onApplied) {
  // At-least-once: ack + bookkeeping only after the handler ran without throwing
  // (ack = event APPLIED, not just received). A failed apply leaves the cursor
  // behind, so the next resync retries the event.
  Promise.resolve(ch.applyFn(data))
    .then(() => {
      if (eventId) {
        markSeen(eventId);
        enqueueAck({ eventId, channel: path });
      }
      if (onApplied) onApplied();
      persist();
    })
    .catch((error) => {
      logger.error(`Handler failed for channel ${ch.name}`, { error });
      reportDeviceEvent('apply_failed', {
        channel: path,
        event_type: data.event?.type || null,
        event_id: eventId,
        error: String(error),
      });
      armRetry(path); // do not wait for the next push to resync what just failed
    });
}

/**
 * Entry point for every event delivered over the WebSocket. Replaces the direct
 * handler call in App.vue's subscribe `next`.
 */
export function onPushEvent(path, data) {
  const ch = channels.get(path);
  if (!ch) return;

  const event = data.event || {};
  const seq = toSeq(event.seq);
  const eventId = event.event_id || null;

  // Publisher not yet on the ledger (pre-2.12 stack): apply + ack, no cursor logic.
  if (seq == null) {
    applyPush(ch, path, data, eventId);
    return;
  }

  if (eventId && seenSet.has(eventId)) return; // already applied (push/resync race)

  const cursor = cursors[path];
  if (cursor == null || seq === cursor + 1) {
    // First seq ever seen on this channel adopts the cursor as-is: resyncing history
    // that predates the login would replay up to 72 h of stale events.
    applyPush(ch, path, data, eventId, () => advanceCursor(path, seq));
  } else if (seq > cursor + 1) {
    // Missed events between cursor and seq. Apply this one NOW (freshest data wins on
    // latency), leave the cursor where it was, and let the resync walk the gap — it
    // advances the cursor past `seq` and dedup skips what we just applied.
    applyPush(ch, path, data, eventId);
    resyncChannel(path, 'gap');
  } else {
    // seq <= cursor: normally a duplicate — but an UNSEEN id at seq 1 (or far below
    // the cursor) means the channel's seq was reset by the dead-channel sweep.
    const looksLikeReset = seq === 1 || cursor - seq >= RESET_MIN_GAP;
    if (looksLikeReset && eventId && !seenSet.has(eventId)) {
      logger.warning(`Seq reset detected on ${path} (cursor ${cursor} → seq ${seq}), adopting new sequence`);
      cursors[path] = seq - 1;
      persist();
      applyPush(ch, path, data, eventId, () => advanceCursor(path, seq));
    }
    // else: genuine duplicate/out-of-order push → discard
  }
}

// ── resync path ─────────────────────────────────────────────────────────────

function buildReplayData(ch, path, row) {
  // Reconstruct the exact wire shape _publish() would have sent, so handlers can't
  // tell a replayed event from a live one (except via _via).
  const event = { ...(row.payload || {}), event_id: row.eventId, seq: row.seq };
  if (row.type && row.type !== 'unknown') event.type = row.type;
  return { event, _channelName: ch.name, _channelPath: path, _via: 'resync' };
}

/**
 * Fetch and apply everything the ledger has after this channel's cursor.
 * Coalesces concurrent calls; retries with backoff while events_since is unreachable.
 */
export async function resyncChannel(path, reason) {
  const ch = channels.get(path);
  if (!ch || !deviceId) return;
  if (HINT_REASONS.has(reason)) {
    // Min with the window: a clock that went backwards must not park the run for hours.
    const waitMs = Math.min((lastSyncAt[path] ?? -Infinity) + HINT_MIN_INTERVAL_MS - Date.now(), HINT_MIN_INTERVAL_MS);
    if (waitMs > 0) {
      armHintTimer(path, reason, waitMs);
      return;
    }
  } else {
    clearHintTimer(path); // this run catches up whatever the pending hint would have
  }
  if (resyncing.has(path)) {
    rerunAfter.add(path); // a gap arrived mid-resync: go around once more
    return;
  }
  resyncing.add(path);
  lastSyncAt[path] = Date.now();
  clearRetry(path);

  // A channel with a seq cursor recovers from it (dense, exact). A cursorless channel
  // bootstraps from its send-time anchor instead: the first page's rows advance the seq
  // cursor, and the loop continues on the seq path. This is what closes the empty-screen
  // hole — a device that booted quiet (no live event, seedCursor failed/raced) used to
  // have every reconnect resync silently skip it because it had no cursor; the anchor is
  // query-free (device clock, cannot fail at boot) so it always has a starting point.
  const startedCursorless = cursors[path] == null;
  if (startedCursorless) seedAnchor(path);
  // A coalesced walk advances these LOCAL copies, never `cursors`/`anchors`: those are persisted
  // by every other code path, and a cursor stored past rows whose ACKs are not enqueued yet
  // would lose them for good if the page died first. They are committed after the refetch and
  // the ACKs. A failed refetch therefore has nothing to rewind (the retry walks the same rows).
  let walkCursor = cursors[path]; // null/undefined: cursorless, the first page comes from the anchor
  let walkAnchor = anchors[path];
  const ackIds = [];

  let replayed = 0;
  let acked = 0;
  let coalescedRows = 0;
  try {
    let page;
    do {
      const from = ch.coalesceResync ? walkCursor : cursors[path];
      const fromAnchor = ch.coalesceResync ? walkAnchor : anchors[path];
      page = from != null
        ? await fetchEventsSince(path, from)
        : await fetchEventsSinceTime(path, fromAnchor);
      for (const row of page) {
        const isNew = !row.eventId || !seenSet.has(row.eventId);
        if (ch.coalesceResync) {
          walkCursor = Math.max(walkCursor ?? 0, row.seq);
          if (row.createdAt && (!walkAnchor || row.createdAt > walkAnchor)) walkAnchor = row.createdAt;
          // At-least-once: nothing is marked seen or ACKed until the refetch below has succeeded.
          if (isNew) {
            coalescedRows += 1;
            if (row.eventId) ackIds.push(row.eventId);
          }
          continue; // one full refetch below instead of N stale replays
        }
        advanceCursor(path, row.seq);
        advanceAnchor(path, row.createdAt); // future cursorless recovery resumes skew-free
        // At-most-once replay: persist cursor + dedup BEFORE applying, so a handler
        // that reloads the page (action `refresh`) can never replay-loop.
        if (row.eventId) {
          markSeen(row.eventId);
          if (isNew) { // a row a live push already applied was ACKed by that push
            enqueueAck({ eventId: row.eventId, channel: path, via: 'resync' });
            acked += 1;
          }
        }
        persist();
        if (!isNew) continue; // already applied via push
        if (row.createdAt && Date.now() - Date.parse(row.createdAt) > MAX_APPLY_AGE_MS) {
          continue; // too old to act on (stale modal/action) — acked above, not applied
        }
        try {
          await ch.applyFn(buildReplayData(ch, path, row));
          replayed += 1;
        } catch (error) {
          logger.error(`Replay handler failed for channel ${ch.name}`, { error });
          reportDeviceEvent('apply_failed', {
            channel: path,
            event_type: row.type || null,
            event_id: row.eventId,
            error: String(error),
            via: 'resync',
          });
        }
      }
    } while (page.length === PAGE_SIZE); // exactly LIMIT rows → there may be more

    if (ch.coalesceResync) {
      if (coalescedRows > 0) {
        // Full refetch: DB state already contains every missed event. If it throws, the committed
        // cursor never moved and nothing was ACKed: the catch below arms the retry.
        await ch.applyFn(null);
        ackIds.forEach(markSeen);
        enqueueAcks(ackIds.map((eventId) => ({ eventId, channel: path, via: 'resync' })));
        acked = ackIds.length;
        replayed = coalescedRows;
      }
      // Commit the walk only now (after the refetch and the ACKs, or with no refetch at all when
      // every row was already seen). Math.max keeps a cursor a live push moved further meanwhile.
      if (walkCursor != null) advanceCursor(path, walkCursor);
      if (walkAnchor) advanceAnchor(path, walkAnchor);
      persist(); // one write for the whole walk (the loop above does none)
    }
    if (acked > 0) flushAcks();
    if (replayed > 0 || acked > 0) {
      logger.success(`Resync ${path} (${reason}): recovered ${replayed} event(s), acked ${acked}`);
      reportForensicEvent('conn_resync', { channel: path, reason, replayed, acked });
    }
    retryMs[path] = RETRY_BASE_MS;
  } catch (error) {
    // events_since[_time] unreachable (offline, or events_since_time not deployed yet).
    // For orders the full DB refetch recovers current state without the ledger at all, so
    // a cursorless boot (empty screen) still recovers even before the time function ships;
    // live pushes cover the rest via the gap branch. Then retry the ledger path with backoff.
    if (startedCursorless && cursors[path] == null && ch.coalesceResync) {
      try {
        await ch.applyFn(null);
        logger.success(`Resync ${path} (${reason}): cursorless fallback refetch`);
      } catch (refetchError) {
        logger.warning(`Cursorless fallback refetch failed for ${path} (${reason})`, { error: refetchError });
      }
    }
    logger.warning(`Resync failed for ${path} (${reason}), will retry`, { error });
    delete lastSyncAt[path]; // a failed run synced nothing: the next hint (back online) must not wait
    armRetry(path);
  } finally {
    resyncing.delete(path);
    if (rerunAfter.delete(path)) resyncChannel(path, 'rerun');
  }
}

export function resyncAllChannels(reason) {
  for (const path of channels.keys()) {
    resyncChannel(path, reason);
  }
}

/** Whether this channel has ever seen a seq-carrying event (i.e. resync can target it). */
export function hasCursor(path) {
  return cursors[path] != null;
}

/**
 * Seed a cursorless channel with the ledger's current max seq — WITHOUT applying
 * anything (the boot fetch already loaded current state). Without this, a device
 * whose session starts quiet (no live event yet) has no cursor, resync skips it,
 * and events published during a disconnection that precedes the first live push
 * are unrecoverable. Best-effort: on failure the channel just stays cursorless
 * (the pre-seeding behavior — first live push adopts its seq).
 */
export async function seedCursor(path) {
  const ch = channels.get(path);
  if (!ch || !deviceId) return;
  if (cursors[path] != null) {
    // A persisted cursor is checked against the server ONCE per session: a cursor above the
    // channel's max seq means the seq restarted while this tablet was off (the dead-channel
    // sweep), and every future event would then read as a duplicate and be dropped.
    if (validated.has(path)) return;
    validated.add(path);
    const before = cursors[path];
    let maxSeq;
    try {
      maxSeq = await fetchChannelMaxSeq(path);
    } catch (error) {
      validated.delete(path); // not checked: the next seedCursor tries again
      logger.console(`Could not validate cursor for ${path}`, error);
      return;
    }
    // Compare-and-set: a push or a resync that moved the cursor meanwhile knows better.
    if (maxSeq < before && cursors[path] === before) {
      cursors[path] = 0;
      persist();
      logger.console(`Cursor for ${path} was ${before}, server max is ${maxSeq}: reset to 0`);
      if (maxSeq > 0) reportForensicEvent('ledger_cursor_reset', { channel: path, cursor: before, server_max: maxSeq });
      resyncChannel(path, 'seq_reset');
    }
    return;
  }
  try {
    const maxSeq = await fetchChannelMaxSeq(path);
    // Whatever the cursor is now came from this session's server state (this max, or a live
    // push meanwhile): nothing to validate, so a socket rebuild does not query it again.
    validated.add(path);
    // A live push may have adopted a (newer) cursor while the query was in flight.
    if (cursors[path] == null) {
      cursors[path] = maxSeq;
      persist();
      logger.console(`Cursor seeded for ${path} at seq ${maxSeq}`);
    }
  } catch (error) {
    logger.console(`Could not seed cursor for ${path}`, error);
  }
}

// ── socket probe ────────────────────────────────────────────────────────────────────────────

/**
 * Fail safe: the probe reads private provider fields, and a release that renames them must make the
 * watchdog go blind (do nothing), never act on `undefined`. A provider counts only when the two
 * fields every rule depends on have the types Amplify 6.18.0 gives them.
 */
function hasUsableFields(provider) {
  return Boolean(provider)
    && typeof provider.socketStatus === 'number'
    && Number.isFinite(provider.keepAliveTimestamp);
}

function isEventsProvider(provider) {
  if (!provider || typeof provider !== 'object') return false;
  if (typeof provider.getProviderName !== 'function') return true; // not Amplify's: tests, future shapes
  try {
    return provider.getProviderName() === EVENTS_PROVIDER_NAME;
  } catch (_) {
    return false;
  }
}

/**
 * Compare the provider's socket with the one we last saw and keep the three timestamps the rules
 * need. Called on every Hub event, on the socket's own `close` and on every probe, so the clocks
 * start when the change happened, not when the next 30 s tick noticed it.
 */
function observeSocket(now = Date.now()) {
  const provider = socketProvider;
  if (!hasUsableFields(provider)) return;
  const ws = provider.awsRealTimeSocket || null;
  if (ws !== trackedSocket) {
    trackedSocket = ws;
    socketSeq += 1;
    socketOpenedAt = ws ? now : null;
    socketClosedAt = null;
    if (ws && typeof ws.addEventListener === 'function') {
      // Public WebSocket API only: record WHEN it closed, so the DEAD clock is exact even when
      // Amplify stays silent (no Hub transition, the whole point).
      ws.addEventListener('close', () => {
        if (trackedSocket !== ws) return;
        socketClosedAt = Date.now();
        observeSocket(socketClosedAt);
      });
    }
  }
  const open = Boolean(ws) && ws.readyState === WS_OPEN && provider.socketStatus === SOCKET_STATUS_READY;
  if (open) {
    notReadySince = null;
  } else if (notReadySince == null) {
    notReadySince = socketClosedAt ?? now;
  }
}

/**
 * What the socket looks like right now, read locally (no request).
 *
 * `kaAgeMs` is measured from the later of the provider's `keepAliveTimestamp` and the moment this
 * socket object was first seen: Amplify does NOT refresh `keepAliveTimestamp` on connection_ack or
 * subscribe_success, so right after a reconnect it still holds the previous socket's last frame.
 * Taken at face value that would read as a zombie for up to a minute after every healthy reconnect.
 *
 * `hasProvider` is false unless the provider has the fields the rules read, with the right types
 * (`socketStatus` a number, `keepAliveTimestamp` finite): an Amplify release that renames them
 * leaves the watchdog doing nothing instead of acting on `undefined`. `socketSeq` changes whenever
 * the provider's socket object does, so "a frame from the NEW socket" is an identity check.
 *
 * @returns {{hasProvider: boolean, wsReadyState: number|null, socketStatus: number|null,
 *   kaAgeMs: number|null, kaTimestamp: number|null, socketOpenedAt: number|null,
 *   socketClosedAt: number|null, notReadySince: number|null, socketSeq: number,
 *   hubState: string|null, hubStateSince: number|null}}
 */
export function getSocketProbe(now = Date.now()) {
  observeSocket(now);
  const provider = socketProvider;
  if (!hasUsableFields(provider)) {
    return {
      hasProvider: false, wsReadyState: null, socketStatus: null, kaAgeMs: null, kaTimestamp: null,
      socketOpenedAt: null, socketClosedAt: null, notReadySince: null, socketSeq,
      hubState: lastConnectionState, hubStateSince,
    };
  }
  const ws = trackedSocket;
  const kaTimestamp = Number.isFinite(provider.keepAliveTimestamp) ? provider.keepAliveTimestamp : null;
  const kaFloor = Math.max(kaTimestamp ?? 0, socketOpenedAt ?? 0);
  return {
    hasProvider: true,
    wsReadyState: ws ? ws.readyState : null,
    socketStatus: provider.socketStatus ?? null,
    kaAgeMs: ws && kaFloor > 0 ? Math.max(0, now - kaFloor) : null,
    kaTimestamp,
    socketOpenedAt,
    socketClosedAt,
    notReadySince,
    socketSeq,
    hubState: lastConnectionState,
    hubStateSince,
  };
}

/** How many channels the ledger tracks. The watchdog does nothing before there is one. */
export function registeredChannelCount() {
  return channels.size;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Close the Events WebSocket and wait until Amplify has registered it (socketStatus CLOSED).
 *
 * Re-subscribing on a dead-but-READY socket reuses it: `connect` and `_initializeWebSocketConnection`
 * return early while socketStatus is READY, the subscribe goes into the void, its ack times out
 * after 15 s, and that timeout clears Amplify's keep-alive interval — a zombie for good. So a
 * recovery must close the socket FIRST. Never `events.closeAll()`: it unsubscribes Amplify's
 * Hub monitor for the rest of the session.
 *
 * @returns {Promise<{hadSocket: boolean, readyStateBefore: number|null, closed: boolean}>}
 *   `closed` false means Amplify still calls the socket READY after `timeoutMs`: the caller must
 *   not re-subscribe over it.
 */
export async function closeEventsSocket(timeoutMs = 2000) {
  const provider = socketProvider;
  // No usable provider (never seen, or its fields are not the ones we know): there is nothing we
  // can close or wait for, so do not wait 2 s for a CLOSED that can never be read.
  if (!hasUsableFields(provider)) return { hadSocket: false, readyStateBefore: null, closed: true };
  const ws = provider.awsRealTimeSocket || null;
  const readyStateBefore = ws ? ws.readyState : null;
  if (ws && (ws.readyState === WS_OPEN || ws.readyState === 0)) {
    try { ws.close(); } catch (error) { logger.console('closeEventsSocket: close() threw', error); }
  }
  const deadline = Date.now() + timeoutMs;
  while (provider.socketStatus !== SOCKET_STATUS_CLOSED && Date.now() < deadline) {
    await sleep(50);
  }
  observeSocket();
  return { hadSocket: Boolean(ws), readyStateBefore, closed: provider.socketStatus === SOCKET_STATUS_CLOSED };
}

function clearLateTracking() {
  lateTracking = null;
  if (lateTrackingTimer) { clearTimeout(lateTrackingTimer); lateTrackingTimer = null; }
}

let recoveryFlight = null; // { id, promise } while a rebuild runs
let flightSeq = 0;

/**
 * A channel's subscribe was acked after a rebuild: NOW its subscription is live, so resync it.
 *
 * Only channels the rebuild has STARTED count. Our close triggers Amplify's own reconnect, which
 * after 5 s re-subscribes the OLD observers the loop has not cleaned up yet; their acks arrive
 * before channel k's new subscription exists, and must neither mark it acked nor resync it early.
 * The ack of the new subscription is the one that counts.
 */
function onSubscriptionAck(path) {
  const tracking = rebuildTracking || lateTracking;
  if (!tracking || !channels.has(path) || !tracking.started.has(path) || tracking.acked.has(path)) return;
  tracking.acked.add(path);
  resyncChannel(path, 'subscribed');
}

/**
 * A channel's subscribe was REJECTED (its observer errored: an invalid channel name, say). It will
 * never be acked, so a rebuild must not wait for it, and it must not make the rebuild look failed:
 * the channel is settled, and resynced once because the ledger is the only way left to hear from it.
 * Called from App.vue's subscribe `error` handler; outside a rebuild it does nothing.
 */
export function noteChannelRejected(path) {
  const tracking = rebuildTracking || lateTracking;
  if (!tracking || !channels.has(path) || !tracking.started.has(path) || tracking.acked.has(path)) return;
  tracking.acked.add(path);
  resyncChannel(path, 'rejected');
}

/**
 * Wait (bounded) for every rebuilt channel's subscribe ack, then resync the ones that never acked
 * anyway, so the gap is covered even when an ack is lost. (A REJECTED channel is settled by
 * noteChannelRejected, so this does not sit out the wait for it.) The fallback does not mark the
 * channel acked: its real ack, when it does come (the tracking stays live for a minute after the
 * rebuild), resyncs it once more, from a moment the subscription is certainly live.
 */
async function settleAcks(tracking, paths, timeoutMs, stopped) {
  const deadline = Date.now() + timeoutMs;
  while (!stopped() && paths.some((path) => !tracking.acked.has(path)) && Date.now() < deadline) await sleep(50);
  if (stopped()) return;
  for (const path of paths) {
    if (!tracking.acked.has(path) && !tracking.fallback.has(path) && channels.has(path)) {
      tracking.fallback.add(path);
      resyncChannel(path, 'rebuilt');
    }
  }
}

/**
 * The recovery primitive: close the socket, rebuild the channels on a fresh one.
 *
 * Requests. A rebuild should cost about six: the order fetch plus one ledger resync per channel.
 * The resync that closes the gap for a channel is the one AFTER ITS OWN subscribe ack. Amplify
 * announces each ack on the Hub, so while a rebuild runs the ledger resyncs each channel when its
 * ack arrives, instead of (a) a resync right after subscribe() is called, which runs before the
 * ack, and (b) the Hub's `reconnected` resync of every channel on the FIRST ack, which runs before
 * the later channels (each waits on its own auth) are subscribed. The caller therefore rebuilds
 * with `resyncOnConnect: false`. A channel whose ack never comes is resynced after a few seconds
 * anyway.
 *
 * Single flight: a second call while one runs gets the same promise (foreground, online and the
 * watchdog can all want a rebuild at once, and two init loops racing on the same subscriptions
 * would orphan some). `isCancelled` is checked after every await so a logout or an expired
 * session stops the loop instead of resubscribing behind it. A flight that has not finished in
 * `timeoutMs` (a connect that hangs) is given up on: its slot is freed under its flight id, so a
 * late finish cannot clobber the next flight, and its loop stops at its next step.
 *
 * @param {object} options
 * @param {Array<string|{path: string}>} options.channels  what to rebuild, in order
 * @param {(channel: object) => Promise<void>} options.reinitChannel
 * @param {() => boolean} [options.isCancelled]
 * @param {number} [options.timeoutMs]
 * @returns {Promise<{hadSocket: boolean, readyStateBefore: number|null, closed: boolean,
 *   rebuilt: boolean, cancelled: boolean, timedOut: boolean}>}
 */
export function recoverEventsSocket({ channels: toRebuild, reinitChannel, isCancelled = () => false, timeoutMs = REBUILD_TIMEOUT_MS }) {
  if (recoveryFlight) return recoveryFlight.promise;
  const id = flightSeq + 1;
  flightSeq = id;
  let timedOut = false;
  const stopped = () => timedOut || isCancelled();
  const tracking = { started: new Set(), acked: new Set(), fallback: new Set() };
  clearLateTracking();
  rebuildTracking = tracking;
  const release = ({ keepLate = false } = {}) => {
    if (recoveryFlight?.id === id) recoveryFlight = null;
    if (rebuildTracking === tracking) {
      rebuildTracking = null;
      // Acks slower than the wait still resync their channel for a minute after the rebuild.
      if (keepLate) {
        lateTracking = tracking;
        lateTrackingTimer = setTimeout(clearLateTracking, LATE_ACK_WINDOW_MS);
      }
    }
  };
  const paths = toRebuild.map((channel) => (typeof channel === 'string' ? channel : channel.path));
  const done = (closeInfo, extra) => ({ ...closeInfo, rebuilt: false, cancelled: false, timedOut: false, ...extra });

  const work = (async () => {
    const closeInfo = await closeEventsSocket();
    if (stopped()) return done(closeInfo, { cancelled: !timedOut, timedOut });
    if (!closeInfo.closed) return done(closeInfo);
    for (const channel of toRebuild) {
      if (stopped()) return done(closeInfo, { cancelled: !timedOut, timedOut });
      tracking.started.add(typeof channel === 'string' ? channel : channel.path);
      await reinitChannel(channel);
    }
    if (stopped()) return done(closeInfo, { cancelled: !timedOut, timedOut });
    await settleAcks(tracking, paths, 5000, stopped);
    if (stopped()) return done(closeInfo, { cancelled: !timedOut, timedOut });
    return done(closeInfo, { rebuilt: true });
  })();

  let timer;
  const promise = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      release();
      resolve(done({ hadSocket: false, readyStateBefore: null, closed: false }, { timedOut: true }));
    }, timeoutMs);
    work.then(resolve, reject);
  }).finally(() => {
    clearTimeout(timer);
    release({ keepLate: !timedOut && !isCancelled() });
  });
  recoveryFlight = { id, promise };
  return promise;
}

// ── lifecycle ───────────────────────────────────────────────────────────────

function onOnline() {
  // Fresh connection: forget the backoff history. While offline, the heartbeat's
  // failed resync attempts double the per-channel retry delay up to 5 min — an
  // 'online' attempt that fires a beat too early (interface up, connectivity not
  // yet usable) would otherwise be rescheduled that far out.
  for (const path of channels.keys()) {
    clearRetry(path);
    retryMs[path] = RETRY_BASE_MS;
  }
  emitConnectivity();
  resyncAllChannels('online');
}
function onOffline() {
  // Interface just went down — surface it immediately (the instant-offline floor).
  // The socket's ConnectionStateChange would confirm it a beat later, but the OS
  // signal is free and instantaneous, which is exactly what the offline UX needs.
  emitConnectivity();
}
function onVisibilityChange() {
  if (document.visibilityState === 'visible') resyncAllChannels('visible');
}

export function initEventLedger(ids) {
  deviceId = ids.deviceId;
  onConnectivity = ids.onConnectivity || null;
  onSocketState = ids.onSocketState || null;
  cursors = {};
  anchors = {};
  seen = [];
  seenSet = new Set();
  validated = new Set();
  for (const path of Object.keys(hintTimers)) clearHintTimer(path);
  lastSyncAt = {};
  const { reason, savedAgeMs } = loadPersisted();
  // Channels registered before this init could not seed (no device id yet): do it now.
  for (const path of channels.keys()) seedAnchor(path);
  reportForensicEvent('ledger_init', {
    reason, channels: Object.keys(cursors).length, saved_age_ms: savedAgeMs,
  });

  if (!listenersArmed) {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
    listenersArmed = true;
  }

  // Seed the consumer with the current value (optimistic: online unless the interface
  // is already down) so it doesn't sit at its default until the first transition.
  emitConnectivity();

  // Amplify emits ConnectionStateChange on the Hub 'api' channel for the Events
  // WebSocket. Resync on every transition INTO any 'Connected*' state from a
  // disrupted one (Connecting/Disconnected/ConnectionDisrupted*): Amplify
  // reconnects the socket internally without re-running App.vue's initializeChannel,
  // so without this hook a silent reconnection would leave the gap unrecovered until
  // the next pushed event happens to reveal it. The first state up is not always
  // 'Connected': Amplify keeps its keep-alive flag 'unhealthy' across a drop, so after
  // a long silence the reconnect goes Connecting → ConnectedPendingKeepAlive → Connected
  // and 'Connected' is entered from a Connected* state. The resync therefore keys on the
  // transition out of the disrupted state, and runs once per reconnect: the later
  // ConnectedPendingKeepAlive → Connected hop is not a disruption and resyncs nothing.
  if (!hubUnsubscribe) {
    hubUnsubscribe = Hub.listen('api', ({ payload }) => {
      // Amplify announces every acked subscribe on the Hub with the channel path in `data.query`.
      if (payload.event === SUBSCRIPTION_ACK_EVENT) {
        onSubscriptionAck(payload.data?.query);
        return;
      }
      if (payload.event !== 'ConnectionStateChange') return;
      const state = payload.data?.connectionState;
      if (!state) return;
      // Keep the provider: it is the only way to read the socket. Cheap, and re-read on every
      // event because Amplify could in principle swap the instance.
      const provider = payload.data?.provider;
      if (isEventsProvider(provider)) socketProvider = provider;
      if (state.startsWith('Connected') && lastConnectionState && !lastConnectionState.startsWith('Connected')) {
        // During a rebuild the resync runs per channel, after ITS OWN subscribe ack (see
        // recoverEventsSocket): this one fires on the FIRST ack, before the later channels'
        // subscriptions are active, and would leave a gap for them.
        if (!rebuildTracking) resyncAllChannels('reconnected');
      }
      const previous = lastConnectionState;
      if (state !== previous) hubStateSince = Date.now();
      lastConnectionState = state;
      observeSocket();
      emitConnectivity();
      // Hand every transition to the consumer. This is the ONLY signal that a socket
      // died silently: Amplify's keep-alive watchdog reports 'ConnectedPendingKeepAlive'
      // (no `ka` for 65s) and then 'ConnectionDisrupted' (no message for
      // connectionTimeoutMs, 5 min by default), but it never errors or completes the
      // per-channel Observables — so `subscription.closed` stays false and a consumer
      // that only inspects the subscription believes the channel is healthy forever.
      if (onSocketState) onSocketState({ state, previous });  // previous kept for future triage
    });
  }
}

/**
 * Track a channel. `coalesceResync` is for handlers that refetch full state and
 * ignore the event payload (orders): gaps become one refetch instead of N replays.
 */
export function registerChannel(path, { name, applyFn, coalesceResync = false }) {
  channels.set(path, { name, applyFn, coalesceResync });
  seedAnchor(path); // query-free floor for a cursorless recovery on this channel
}

/** This channel's last contiguously-applied seq, or null before it has one. */
export function getCursor(path) {
  return cursors[path] ?? null;
}

/** A copy of the ledger's state for diagnostics; changing it changes nothing. */
export function getLedgerState() {
  return { deviceId, cursors: { ...cursors }, savedAt };
}

export function stopEventLedger() {
  deviceId = null; // a later registerChannel must not write state nobody can attribute
  savedAt = null;
  for (const path of Object.keys(retryTimers)) clearRetry(path);
  for (const path of Object.keys(hintTimers)) clearHintTimer(path);
  lastSyncAt = {};
  channels = new Map();
  resyncing = new Set();
  rerunAfter = new Set();
  if (listenersArmed) {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listenersArmed = false;
  }
  if (hubUnsubscribe) {
    hubUnsubscribe();
    hubUnsubscribe = null;
    lastConnectionState = null;
  }
  socketProvider = null;
  trackedSocket = null;
  socketOpenedAt = null;
  socketClosedAt = null;
  notReadySince = null;
  hubStateSince = null;
  recoveryFlight = null;
  rebuildTracking = null;
  clearLateTracking();
  onConnectivity = null;
  onSocketState = null;
  lastEmittedOnline = null;
}
